import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from './supabase';
import { useStores } from './useStores';
import AIReport from './AIReport';
import { Card, Pill, Mono, GridBg, IconTile } from './components/ui';
import { productKeyStable } from './productKey';
import { boundaryDateAcross } from './priceHistoryDates';
import { fetchAllPages } from './fetchAllPages';
import { useLatestRequest } from './useLatestRequest';

// Keep in lockstep with the Price Alerts page (PriceChangeAlerts.js):
// its THRESHOLDS default is 10%, so the dashboard preview must use the
// same floor or the widget count won't reconcile with the page.
const ALERT_THRESHOLD = 10;

function parsePrice(val) {
  if (!val) return 0;
  return parseFloat(String(val).replace(/[^0-9.]/g, '')) || 0;
}

function effectivePrice(row) {
  const reg  = parsePrice(row.regular_price);
  const sale = parsePrice(row.sale_price);
  return sale > 0 && sale < reg ? sale : reg;
}

// Auto-scrolls overflowing text using a single shared @keyframes rule
// (defined in index.css). Each instance sets its measured overflow on a
// CSS custom property — no per-row <style> tag injection.
function ScrollingName({ text, className = '', style }) {
  const containerRef = useRef(null);
  const textRef      = useRef(null);
  const [animStyle, setAnimStyle] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!containerRef.current || !textRef.current) return;
      const overflow = textRef.current.scrollWidth - containerRef.current.clientWidth;
      if (overflow > 4) {
        const duration = Math.max(4, overflow / 40);
        const delay    = Math.random() * 1.5;
        setAnimStyle({
          animation: `gv-scroll-name ${duration}s ${delay}s linear infinite`,
          whiteSpace: 'nowrap',
          display: 'inline-block',
          '--gv-scroll-offset': `-${overflow}px`,
        });
      } else {
        setAnimStyle(null);
      }
    }, 150);

    return () => clearTimeout(t);
  }, [text]);

  return (
    <div ref={containerRef} className="overflow-hidden">
      <span ref={textRef} className={className} style={{ ...style, ...(animStyle || {}) }}>
        {text}
      </span>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function subtractDays(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

const DIFF_COLS = 'product_name, store_id, regular_price, sale_price, brand, category, size_value, size_unit, pack_count, sku_hash';

// Full-day snapshot, offset-paginated. Only used as the fallback when there
// are no matched pairs to scope by — on non-latest dates this query shape
// (big store_id in-list + eq date + order id + offset) exceeds the Supabase
// statement timeout once price_history is large, which is why errors MUST
// throw here: swallowing them made a timed-out week-old snapshot look like
// "no rows", which zeroed the whole Price Alerts widget.
async function fetchSnapshotForDiff(compIds, date) {
  let rows = [];
  let from = 0;
  const BATCH = 1000;
  while (true) {
    const { data, error } = await supabase
      .from('price_history')
      .select(DIFF_COLS)
      .in('store_id', compIds)
      .eq('scraped_date', date)
      .order('id')
      .range(from, from + BATCH - 1);
    if (error) throw error;
    if (!data?.length) break;
    rows = rows.concat(data);
    if (data.length < BATCH) break;
    from += BATCH;
  }
  return rows;
}

// Snapshot scoped to the matched competitor SKUs — same approach as the
// Price Alerts page's fetchMatchedSnapshot. Filtering by sku_hash rides the
// price_history (sku_hash, scraped_date) index, so week-old dates return in
// ~1s where the full-day scan above times out. Each (date, 300-hash) slice
// stays under the 1000-row cap (a hash appears at most once per store per
// day), so no offset pagination is needed.
async function fetchMatchedSnapshotForDiff(compIds, compHashes, date) {
  if (!compHashes.length) return [];
  const chunks = [];
  for (let i = 0; i < compHashes.length; i += 300) chunks.push(compHashes.slice(i, i + 300));
  const results = await Promise.all(chunks.map(c => supabase
    .from('price_history')
    .select(DIFF_COLS)
    .in('store_id', compIds)
    .in('sku_hash', c)
    .eq('scraped_date', date)));
  for (const r of results) if (r.error) throw r.error;
  return results.flatMap(r => r.data || []);
}

// Competitor SKUs matched to one of my store's products. Mirrors the Price
// Alerts page gate (PriceChangeAlerts.js fetchMatchedPairs): status not
// rejected, confidence >= 0.6, both sku_hashes present. Returns the
// "compStoreId|compSkuHash" key set (the feed gate) plus the distinct
// competitor hash list (what scopes the snapshot queries above). Returns
// null when there's no my-store or no matches — callers then leave the feed
// ungated, exactly like the page.
async function fetchMatchedPairKeys(myStoreId) {
  if (!myStoreId) return null;
  const rows = await fetchAllPages((withCount) => supabase
    .from('product_matches')
    .select('comp_store_id, comp_sku_hash', withCount ? { count: 'exact' } : undefined)
    .eq('my_store_id', myStoreId)
    .neq('status', 'rejected')
    .gte('confidence', 0.6)
    .not('comp_sku_hash', 'is', null)
    .not('my_sku_hash',  'is', null)
    .order('id'));
  if (!rows.length) return null;
  return {
    keys:   new Set(rows.map(r => `${r.comp_store_id}|${r.comp_sku_hash}`)),
    hashes: [...new Set(rows.map(r => r.comp_sku_hash))],
  };
}

function countDiff(endData, startData, matchedPairs) {
  const makeMap = (rows) => {
    const map = {};
    rows.forEach(r => {
      const p = effectivePrice(r);
      if (p > 0) map[productKeyStable(r)] = { price: p, storeId: r.store_id, skuHash: r.sku_hash };
    });
    return map;
  };
  const endMap   = makeMap(endData);
  const startMap = makeMap(startData);
  let count = 0;
  Object.keys(endMap).forEach(key => {
    const curr = endMap[key];
    const prev = startMap[key];
    if (!prev || curr.price === 0 || prev.price === 0) return;
    if (Math.abs(curr.price - prev.price) < 0.01) return;
    const pct = Math.abs((curr.price - prev.price) / prev.price * 100);
    if (pct < ALERT_THRESHOLD) return;
    // Same sku_hash gate as the live feed below, so the last-week trend
    // baseline is computed on the same matched population as this week.
    const hashKey = curr.skuHash ? `${curr.storeId}|${curr.skuHash}` : null;
    if (matchedPairs && (!hashKey || !matchedPairs.has(hashKey))) return;
    count++;
  });
  return count;
}

function countGenuineSales(rows) {
  return rows.filter(r => {
    const reg  = parsePrice(r.regular_price);
    const sale = parsePrice(r.sale_price);
    return reg > 0 && sale > 0 && sale < reg;
  }).length;
}

function TrendDelta({ thisWeek, lastWeek, loading, inverted = false }) {
  if (loading || lastWeek === null) {
    return (
      <Mono size={12} color="var(--g-text-faint)" style={{ marginTop: 4, display: 'block' }}>
        — vs last week
      </Mono>
    );
  }
  const delta  = thisWeek - lastWeek;
  const isUp   = delta > 0;
  const isFlat = delta === 0;
  // For metrics where "up" is bad (e.g. competitor price changes), invert color
  const goodIsDown = inverted;
  const color  = isFlat
    ? 'var(--g-price-neutral)'
    : (isUp ^ goodIsDown) ? 'var(--g-price-worse)' : 'var(--g-price-better)';
  const arrow = isFlat ? '→' : isUp ? '↑' : '↓';
  const sign  = isUp ? '+' : '';
  return (
    <Mono size={12} color={color} style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
      <span>{arrow} {sign}{delta.toLocaleString()} vs last week</span>
    </Mono>
  );
}

function PreviewCard({ title, accent = 'var(--g-accent-hi)', onViewAll, viewAllLabel = 'View all', children, footer }) {
  return (
    <Card style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 18px',
          borderBottom: '1px solid var(--g-border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 3, height: 14, background: accent, borderRadius: 1.5 }} />
          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--g-text)' }}>{title}</h4>
        </div>
        {onViewAll && (
          <button
            onClick={onViewAll}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
                            color: accent,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {viewAllLabel}
            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>arrow_forward</span>
          </button>
        )}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>{children}</div>
      {footer}
    </Card>
  );
}

export default function DashboardOverview({ myStoreId, myStores = [], stores = [], myStoreName, onNavigate }) {
  const { compIds } = useStores({ myStoreId, myStores, stores });

  // Resolve store names at render time. Keeping the map out of the fetch
  // dependency lets a store-rename re-render the preview lists without
  // re-running every fetch (which would re-query products, prices, etc.).
  const storeNameById = useMemo(
    () => new Map(stores.map(s => [s.id, s.name])),
    [stores]
  );
  const compIdSet = useMemo(() => new Set(compIds), [compIds]);

  const [quickLoading, setQuickLoading]         = useState(true);
  const [quickError, setQuickError]             = useState(false);
  const [latestDate, setLatestDate]             = useState(null);
  const [newProducts, setNewProducts]           = useState([]);
  const [activeSales, setActiveSales]           = useState([]);
  const [newProductCount, setNewProductCount]   = useState(0);
  const [activeSaleCount, setActiveSaleCount]   = useState(0);
  const [lastWeekNewCount, setLastWeekNewCount] = useState(null);
  const [lastWeekSaleCount, setLastWeekSaleCount] = useState(null);

  const [alertsLoading, setAlertsLoading]         = useState(true);
  const [alertsError, setAlertsError]             = useState(false);
  const [topAlerts, setTopAlerts]                 = useState([]);
  const [alertCount, setAlertCount]               = useState(0);
  const [lastWeekAlertCount, setLastWeekAlertCount] = useState(null);
  const [mostActiveStore, setMostActiveStore]     = useState(null);

  // Competitor best sellers — nightly-computed estimates (best_seller_estimates):
  // Jane's reported sales rank where available, restock-cycle velocity otherwise.
  const [bestSellers, setBestSellers] = useState(null); // null = loading

  useEffect(() => {
    if (!compIds.length) return; // stores not loaded yet; stay in the loading state
    let cancelled = false;
    (async () => {
      // Paginate with a stable order instead of a bare limit(5000): an
      // unordered cap silently drops rows/stores once the table outgrows it
      // (same class as the .range() pagination bug).
      // Single-window rows only: since the 7/30/90d estimator landed, the
      // table holds each product up to three times (once per window). Ranking
      // across mixed windows both fetched ~2.5x the rows needed and could
      // show one product several times in the top 5 ("Wild Raspberry" is #1
      // in all three windows). 30d matches the Best Sellers page's default;
      // the is-null arm keeps pre-migration rows working.
      // Competitor + not-delisted filtering happens in the query (it used to
      // pull every store's rows and filter here); topBestSellers still
      // re-checks both, so the card can't show a stale or own-store row.
      try {
        const rows = await fetchAllPages((withCount) => supabase
          .from('best_seller_estimates')
          .select('store_id, product_name, brand, category, cycles, avg_jane_rank, delisted, window_days',
                  withCount ? { count: 'exact' } : undefined)
          .or('window_days.eq.30,window_days.is.null')
          .in('store_id', compIds)
          .not('delisted', 'is', true)
          .order('id'));
        if (!cancelled) setBestSellers(rows);
      } catch (err) {
        // [] renders the "estimates appear after the nightly run" empty state;
        // log the real cause rather than pretending the pipeline hasn't run.
        console.error('[dashboard] best-seller preview fetch failed:', err);
        if (!cancelled) setBestSellers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [compIds]);

  const topBestSellers = useMemo(() => {
    if (!bestSellers) return [];
    // Omit delisted estimates — a "best seller" the competitor no longer
    // carries is misleading in this compact top-5 (the full Best Sellers page
    // keeps them but flags them "not listed today"). !r.delisted keeps
    // false/null and drops only true.
    const comp = bestSellers.filter(r => compIdSet.has(r.store_id) && !r.delisted);
    // Reported ranks lead (lower = better-selling), churn fills the rest
    const ranked  = comp.filter(r => r.avg_jane_rank != null).sort((a, b) => a.avg_jane_rank - b.avg_jane_rank);
    const churned = comp.filter(r => r.avg_jane_rank == null && r.cycles > 0).sort((a, b) => b.cycles - a.cycles);
    return [...ranked, ...churned].slice(0, 5);
  }, [bestSellers, compIdSet]);

  // A competitor-set change (e.g. radius) mid-fetch overlaps requests; every
  // state write after an await checks the request is still the newest.
  const beginQuickRequest  = useLatestRequest();
  const beginAlertsRequest = useLatestRequest();

  const fetchQuick = useCallback(async () => {
    const isCurrent = beginQuickRequest();
    // Competitors only — these cards surface competitor activity, so they
    // reconcile with the New Products / Sales pages' "At Competitor Stores"
    // / "Competitor Discounts" sub-stats, not the all-stores headline.
    if (!compIds.length) { setQuickLoading(false); return; }
    try {

    const { data: dateRow, error: dateErr } = await supabase
      .from('products')
      .select('scraped_date')
      .in('store_id', compIds)
      .not('scraped_date', 'is', null)
      .order('scraped_date', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (dateErr) throw dateErr;
    if (!isCurrent()) return;

    const latest = dateRow?.scraped_date;
    if (!latest) { setQuickLoading(false); return; }
    setLatestDate(latest);

    // A 7-day window ending at `latest` starts at latest-6 (subtracting the
    // full 7 made the window 8 days wide against a 7-day baseline). Keep in
    // lockstep with the New Products page's window math.
    const weekCutoff     = subtractDays(latest, 6);
    const prevWeekCutoff = subtractDays(latest, 13);

    // A true 7-day window, matching the "· 1w" label and the New Products
    // page's 7d option (the old eq-latest form counted a single day, so the
    // card read ~1/5 of what the page it links to shows). The prior-week
    // baseline is products first seen in the preceding 7 days that are
    // still listed — delisted-since items drop out of the products table,
    // so the trend is a floor, not an exact prior-week count.
    const fetchNewProducts = async () => {
      const [newThisRes, newLastRes] = await Promise.all([
        supabase
          .from('products')
          .select('name, brand, category, store_id, regular_price', { count: 'exact' })
          .in('store_id', compIds)
          .gte('first_seen_date', weekCutoff)
          .lte('first_seen_date', latest)
          .not('regular_price', 'is', null)
          .gt('regular_price', 0)
          .order('first_seen_date', { ascending: false })
          .order('name')
          .limit(5),
        supabase
          .from('products')
          .select('name', { count: 'exact', head: true })
          .in('store_id', compIds)
          .gte('first_seen_date', prevWeekCutoff)
          .lt('first_seen_date', weekCutoff)
          .not('regular_price', 'is', null)
          .gt('regular_price', 0),
      ]);
      if (newThisRes.error) throw newThisRes.error;
      if (newLastRes.error) throw newLastRes.error;
      if (!isCurrent()) return;

      if (newThisRes.data) {
        setNewProducts(newThisRes.data.map(p => ({
          ...p,
          price: parsePrice(p.regular_price),
        })));
        setNewProductCount(newThisRes.count || 0);
      }
      setLastWeekNewCount(newLastRes?.count ?? null);
    };

    const fetchLastWeekSaleCount = async () => {
      // Nearest scraped day at least a week back — the sales-trend baseline.
      // Probed against price_history: the products table only holds each
      // store's latest scrape, so a week-old date exists there only as stale
      // leftovers that happen not to have been re-scraped. Per-store probe:
      // the in-list + order-desc form took ~1s and blocked everything below.
      const prevWeekDate = await boundaryDateAcross(compIds, { lte: subtractDays(latest, 7) });
      if (!prevWeekDate) return;
      // price_history, NOT products: products is delete+re-insert and holds
      // only each store's latest scrape, so a week-old scraped_date there
      // matches nothing but stale leftovers (~9 rows — the "+9,849 vs last
      // week" bug). Query per store so each hits the (store_id, scraped_date)
      // index; the combined in-list day-scan form times out on old dates.
      const perStore = await Promise.all(compIds.map(async (sid) => {
        let rows = [];
        let from = 0;
        while (true) {
          const { data, error } = await supabase
            .from('price_history')
            .select('regular_price, sale_price')
            .eq('store_id', sid)
            .eq('scraped_date', prevWeekDate)
            .not('sale_price', 'is', null)
            .order('id')
            .range(from, from + 999);
          if (error) throw error;
          if (!data?.length) break;
          rows = rows.concat(data);
          if (data.length < 1000) break;
          from += 1000;
        }
        return rows;
      }));
      if (!isCurrent()) return;
      setLastWeekSaleCount(countGenuineSales(perStore.flat()));
    };

    const fetchActiveSales = async () => {
      // No scraped_date filter — same as the Sales page. The products table
      // is delete+re-insert, so it already holds only the latest row per
      // store; gating on a single global `latest` would silently drop any
      // store scraped on a different day (the staggered-scrape case).
      const salesRows = await fetchAllPages((withCount) => supabase
        .from('products')
        .select('name, brand, category, store_id, regular_price, sale_price',
                withCount ? { count: 'exact' } : undefined)
        .in('store_id', compIds)
        .not('sale_price', 'is', null)
        .order('id'));
      const genuine = salesRows
        .map(r => {
          const reg  = parsePrice(r.regular_price);
          const sale = parsePrice(r.sale_price);
          if (!reg || !sale || sale >= reg) return null;
          const pct = ((reg - sale) / reg * 100);
          return { ...r, reg, sale, pct };
        })
        .filter(Boolean)
        .sort((a, b) => b.pct - a.pct);
      if (!isCurrent()) return;
      setActiveSaleCount(genuine.length);
      setActiveSales(genuine.slice(0, 5));
    };

    // The three branches share nothing beyond `latest` — run them
    // concurrently so the slowest one sets the wall-clock cost, not the sum.
    await Promise.all([fetchNewProducts(), fetchLastWeekSaleCount(), fetchActiveSales()]);

    } catch (err) {
      // Surface the failure instead of rendering zeros that read as "all
      // quiet" — a timed-out query is not the same as no activity.
      if (!isCurrent()) return;
      console.error('[dashboard] quick stats fetch failed:', err);
      setQuickError(true);
    } finally {
      if (isCurrent()) setQuickLoading(false);
    }
    // `stores` intentionally not a dep — store names are resolved at render
    // via storeNameById, so a rename shouldn't re-trigger this fetch.
  }, [compIds, beginQuickRequest]);

  const fetchAlerts = useCallback(async () => {
    const isCurrent = beginAlertsRequest();
    if (!compIds.length) { setAlertsLoading(false); return; }
    try {

    // Matched pairs only need myStoreId — start the fetch now so it overlaps
    // the date probes below instead of adding its own round trips after them.
    // Their hash list scopes the snapshot queries to matched SKUs, which is
    // what keeps week-old snapshots inside the statement timeout. The no-op
    // catch keeps an early return below from surfacing an unhandled
    // rejection; the real error still propagates where the promise is
    // awaited.
    const matchedPromise = fetchMatchedPairKeys(myStoreId);
    matchedPromise.catch(() => {});

    // Per-store probes (boundaryDateAcross) throughout: the in-list +
    // order-desc form the probes used before measured 1.6s each even warm.
    const latest = await boundaryDateAcross(compIds);
    if (!isCurrent()) return;
    if (!latest) { setAlertsLoading(false); return; }

    // The latest-7 boundary serves double duty: it is this week's start AND
    // last week's end. Probing it twice (as separate targetWeekStart /
    // targetLastWeekEnd queries) also made the snapshot step fetch that
    // date's rows twice.
    const [weekBoundary, lastWeekStart] = await Promise.all([
      boundaryDateAcross(compIds, { lte: subtractDays(latest, 7) }),
      boundaryDateAcross(compIds, { lte: subtractDays(latest, 14) }),
    ]);

    let thisWeekStart = weekBoundary;
    if (!thisWeekStart) {
      thisWeekStart = await boundaryDateAcross(compIds, { ascending: true });
    }
    if (!isCurrent()) return;
    if (!thisWeekStart || thisWeekStart === latest) { setAlertsLoading(false); return; }

    const lastWeekEnd = weekBoundary;

    // With no matches at all (gate off, feed ungated — same as the page) we
    // fall back to the full-day scan.
    const matched = await matchedPromise;
    const matchedPairs = matched?.keys ?? null;

    // One fetch per distinct date: thisWeekStart === lastWeekEnd by
    // construction, and sparse history can collapse other boundaries too.
    // Sharing the promise (and thus the row array) is safe — consumers only
    // read the rows.
    const snapCache = new Map();
    const snap = (date) => {
      if (!snapCache.has(date)) {
        snapCache.set(date, matched
          ? fetchMatchedSnapshotForDiff(compIds, matched.hashes, date)
          : fetchSnapshotForDiff(compIds, date));
      }
      return snapCache.get(date);
    };

    const hasLastWeek = lastWeekEnd && lastWeekStart && lastWeekEnd !== lastWeekStart;
    const [thisEndData, thisStartData, lastEndData, lastStartData] = await Promise.all([
      snap(latest),
      snap(thisWeekStart),
      hasLastWeek ? snap(lastWeekEnd)   : Promise.resolve([]),
      hasLastWeek ? snap(lastWeekStart) : Promise.resolve([]),
    ]);
    if (!isCurrent()) return;

    if (hasLastWeek) {
      setLastWeekAlertCount(countDiff(lastEndData, lastStartData, matchedPairs));
    }

    // Store the row identity on the value side. productKeyStable() (size +
    // cutover-stable sku_hash) is enough to disambiguate even two SKUs that
    // share a display name, but we still need storeId / name to render —
    // pulling them off the row avoids string-splitting a key whose size
    // segment can itself contain pipe characters.
    const makeMap = (rows) => {
      const map = {};
      rows.forEach(r => {
        const price = effectivePrice(r);
        if (price > 0) {
          map[productKeyStable(r)] = { price, storeId: r.store_id, name: r.product_name, brand: r.brand, category: r.category, skuHash: r.sku_hash };
        }
      });
      return map;
    };
    const endMap   = makeMap(thisEndData);
    const startMap = makeMap(thisStartData);

    const changes = [];
    const storeCounts = {};
    Object.keys(endMap).forEach(key => {
      const curr = endMap[key];
      const prev = startMap[key];
      if (!prev || curr.price === 0 || prev.price === 0) return;
      if (Math.abs(curr.price - prev.price) < 0.01) return;
      const pct = ((curr.price - prev.price) / prev.price) * 100;
      if (Math.abs(pct) < ALERT_THRESHOLD) return;

      const storeId = curr.storeId;
      // Defensive: query already filters by compIds, but skip anything that
      // somehow slipped through. compIdSet keeps this O(1) without pulling
      // competitorStores into the dep array.
      if (!compIdSet.has(storeId)) return;

      // Same gate as the Price Alerts page: only surface competitor SKUs
      // matched to one of my products, keyed on sku_hash. This both filters
      // out competitor-only products (which never appear on the page) and
      // kills the productKey-collision spikes, since a collapsed key whose
      // row carries the wrong variant's sku_hash won't match a real pair.
      const hashKey = curr.skuHash ? `${storeId}|${curr.skuHash}` : null;
      if (matchedPairs && (!hashKey || !matchedPairs.has(hashKey))) return;

      storeCounts[storeId] = (storeCounts[storeId] || 0) + 1;
      changes.push({
        name: curr.name, storeId, brand: curr.brand, category: curr.category,
        startPrice: prev.price, endPrice: curr.price, pct,
        direction: curr.price > prev.price ? 'up' : 'down',
      });
    });

    changes.sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct));
    setAlertCount(changes.length);
    setTopAlerts(changes.slice(0, 5));

    const topId = Object.keys(storeCounts).sort((a, b) => storeCounts[b] - storeCounts[a])[0];
    if (topId) {
      setMostActiveStore({ storeId: topId, count: storeCounts[topId] });
    }

    } catch (err) {
      // Surface the failure instead of rendering "No changes ≥ 10%" — a
      // timed-out snapshot is not the same as a quiet week (this exact
      // failure mode zeroed the widget while the Price Alerts page had
      // hundreds of changes).
      if (!isCurrent()) return;
      console.error('[dashboard] price alerts fetch failed:', err);
      setAlertsError(true);
    } finally {
      if (isCurrent()) setAlertsLoading(false);
    }
  }, [compIds, compIdSet, myStoreId, beginAlertsRequest]);

  useEffect(() => {
    fetchQuick();
    fetchAlerts();
  }, [fetchQuick, fetchAlerts]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  }, []);

  const nowStr = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }, []);

  const kpiCards = [
    {
      id: 'alerts',
      icon: 'price_change',
      tone: 'amber',
      label: 'Price moves · 1w',
      value: alertsLoading || alertsError ? '—' : alertCount.toLocaleString(),
      loading: alertsLoading,
      trend: alertsError
        ? <Mono size={12} color="var(--g-warn)" style={{ marginTop: 4, display: 'block' }}>Data unavailable</Mono>
        : <TrendDelta thisWeek={alertCount} lastWeek={lastWeekAlertCount} loading={alertsLoading} />,
      onClick: () => onNavigate('Price Alerts'),
    },
    {
      id: 'newproducts',
      icon: 'new_releases',
      tone: 'vine',
      label: 'New products · 1w',
      value: quickLoading || quickError ? '—' : newProductCount.toLocaleString(),
      loading: quickLoading,
      trend: quickError
        ? <Mono size={12} color="var(--g-warn)" style={{ marginTop: 4, display: 'block' }}>Data unavailable</Mono>
        : <TrendDelta thisWeek={newProductCount} lastWeek={lastWeekNewCount} loading={quickLoading} />,
      onClick: () => onNavigate('New Products'),
    },
    {
      id: 'sales',
      icon: 'local_offer',
      tone: 'amber',
      label: 'Active sales',
      value: quickLoading || quickError ? '—' : activeSaleCount.toLocaleString(),
      loading: quickLoading,
      trend: quickError
        ? <Mono size={12} color="var(--g-warn)" style={{ marginTop: 4, display: 'block' }}>Data unavailable</Mono>
        : <TrendDelta thisWeek={activeSaleCount} lastWeek={lastWeekSaleCount} loading={quickLoading} />,
      onClick: () => onNavigate('Sales & Discounts'),
    },
    {
      id: 'rival',
      icon: 'storefront',
      tone: 'vine',
      label: 'Most active rival',
      value: alertsLoading || alertsError ? '—' : (storeNameById.get(mostActiveStore?.storeId) || '—'),
      valueSmall: !alertsLoading && (storeNameById.get(mostActiveStore?.storeId) || '').length > 12,
      sub: alertsLoading ? '' : alertsError ? 'Data unavailable' : mostActiveStore ? `${mostActiveStore.count} price changes` : 'No changes detected',
      loading: alertsLoading,
      onClick: () => onNavigate('Price Alerts'),
    },
  ];

  return (
    <div
      style={{
        position: 'relative',
        padding: 28,
        background: 'var(--g-bg)',
        minHeight: '100%',
      }}
    >
      <GridBg opacity={0.35} />
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          maxWidth: 1600,
          margin: '0 auto',
          width: '100%',
        }}
      >
        {/* Greeting */}
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 700,
              color: 'var(--g-text)',
              letterSpacing: '-0.015em',
            }}
          >
            {greeting},{' '}
            <span style={{ color: 'var(--g-accent-hi)', textShadow: '0 0 18px rgba(16,185,129,0.4)' }}>
              {myStoreName}
            </span>
          </h3>
          <Mono size={12} color="var(--g-text-dim)" style={{ marginTop: 4, display: 'block' }}>
            {latestDate ? `${nowStr} · last scraped ${formatDate(latestDate)}` : 'Loading your competitive intelligence…'}
          </Mono>
        </div>

        {/* AI Daily Brief */}
        <AIReport myStoreId={myStoreId} />

        {/* KPI strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {kpiCards.map(k => (
            <button
              key={k.id}
              onClick={k.onClick}
              style={{
                position: 'relative',
                background: 'var(--g-card)',
                border: '1px solid var(--g-border)',
                borderRadius: 10,
                padding: 16,
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'border-color 0.15s, transform 0.15s',
                fontFamily: 'Inter, sans-serif',
                color: 'var(--g-text)',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(16,185,129,0.30)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--g-border)'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <IconTile icon={k.icon} tone={k.tone} size={26} fontSize={14} />
                <Mono size={12} color="var(--g-text-muted)" style={{ letterSpacing: '0.01em', }}>{k.label}</Mono>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    className={k.loading ? 'animate-pulse' : ''}
                    style={{
                      fontSize: k.valueSmall ? 16 : 26,
                      fontWeight: 700,
                      color: k.loading ? 'var(--g-skeleton)' : 'var(--g-text)',
                      letterSpacing: '-0.02em',
                      lineHeight: 1.1,
                      fontFamily: "'JetBrains Mono', monospace",
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {k.value}
                  </div>
                  {k.sub ? (
                    <Mono size={12} color="var(--g-text-dim)" style={{ marginTop: 4, display: 'block' }}>{k.sub}</Mono>
                  ) : k.trend}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Preview panels — 2×2 grid: Price Alerts, New Products, Active Sales, Best Sellers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>

          {/* Price Alerts Preview */}
          <PreviewCard
            title="Price Alerts"
            accent="var(--g-accent-hi)"
            onViewAll={() => onNavigate('Price Alerts')}
            footer={!alertsLoading && !alertsError && alertCount > 5 ? (
              <button
                onClick={() => onNavigate('Price Alerts')}
                style={{
                  borderTop: '1px solid var(--g-border)',
                  padding: '10px 18px',
                                    fontSize: 12,
                  color: 'var(--g-text-muted)',
                  background: 'transparent',
                  border: 'none',
                  borderTopWidth: 1,
                  cursor: 'pointer',
                  textAlign: 'center',
                  letterSpacing: '0.01em',                                   }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--g-accent-hi)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--g-text-muted)'}
              >
                + {(alertCount - 5).toLocaleString()} more changes
              </button>
            ) : null}
          >
            {alertsLoading ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <Mono size={12} color="var(--g-text-dim)">Scanning prices…</Mono>
              </div>
            ) : alertsError ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <Mono size={12} color="var(--g-warn)">Couldn't load price data — try the Price Alerts page</Mono>
              </div>
            ) : topAlerts.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <Mono size={12} color="var(--g-text-dim)">No changes ≥ {ALERT_THRESHOLD}% this week</Mono>
              </div>
            ) : (
              topAlerts.map((c, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '4px 1fr auto',
                    gap: 12,
                    padding: '12px 18px',
                    alignItems: 'center',
                    borderBottom: i === topAlerts.length - 1 ? 'none' : '1px solid var(--g-border-faint)',
                  }}
                >
                  <div
                    style={{
                      width: 4,
                      height: 32,
                      background: c.direction === 'up' ? 'var(--g-price-worse)' : 'var(--g-price-better)',
                      borderRadius: 2,
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <ScrollingName
                      text={c.name}
                      style={{ fontSize: 13, color: 'var(--g-text)', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}
                    />
                    <Mono size={12} color="var(--g-text-muted)" style={{ marginTop: 2, display: 'block' }}>
                      {[c.brand, c.category, storeNameById.get(c.storeId)].filter(Boolean).join(' · ')}
                    </Mono>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Mono size={12} color="var(--g-text-faint)" style={{ textDecoration: 'line-through', display: 'block' }}>
                      ${c.startPrice.toFixed(2)}
                    </Mono>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, justifyContent: 'flex-end' }}>
                      <Mono size={14} color="var(--g-text)" style={{ fontWeight: 700 }}>${c.endPrice.toFixed(2)}</Mono>
                      <Mono size={12} color={c.direction === 'up' ? 'var(--g-price-worse)' : 'var(--g-price-better)'} style={{ fontWeight: 600 }}>
                        {c.direction === 'up' ? '+' : ''}{c.pct.toFixed(1)}%
                      </Mono>
                    </div>
                  </div>
                </div>
              ))
            )}
          </PreviewCard>

          {/* New Products Preview */}
          <PreviewCard
            title="New Products"
            accent="var(--g-accent-hi)"
            onViewAll={() => onNavigate('New Products')}
            footer={!quickLoading && !quickError && newProductCount > 5 ? (
              <button
                onClick={() => onNavigate('New Products')}
                style={{
                  borderTop: '1px solid var(--g-border)',
                  padding: '10px 18px',
                                    fontSize: 12,
                  color: 'var(--g-text-muted)',
                  background: 'transparent',
                  border: 'none',
                  borderTopWidth: 1,
                  cursor: 'pointer',
                  textAlign: 'center',
                  letterSpacing: '0.01em',                                   }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--g-accent-hi)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--g-text-muted)'}
              >
                + {(newProductCount - 5).toLocaleString()} more new products
              </button>
            ) : null}
          >
            {quickLoading ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <Mono size={12} color="var(--g-text-dim)">Loading…</Mono>
              </div>
            ) : quickError ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <Mono size={12} color="var(--g-warn)">Couldn't load data — try the New Products page</Mono>
              </div>
            ) : newProducts.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <Mono size={12} color="var(--g-text-dim)">No new products this week</Mono>
              </div>
            ) : (
              newProducts.map((p, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 18px',
                    borderBottom: i === newProducts.length - 1 ? 'none' : '1px solid var(--g-border-faint)',
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Pill tone="vine" mono style={{ flexShrink: 0 }}>NEW</Pill>
                      <ScrollingName
                        text={p.name}
                        style={{ fontSize: 13, color: 'var(--g-text)', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}
                      />
                    </div>
                    <Mono size={12} color="var(--g-text-muted)" style={{ marginTop: 2, display: 'block' }}>
                      {[p.brand, p.category, storeNameById.get(p.store_id)].filter(Boolean).join(' · ')}
                    </Mono>
                  </div>
                  <Mono size={13} color="var(--g-text)" style={{ fontWeight: 700, flexShrink: 0 }}>
                    {p.price > 0 ? `$${p.price.toFixed(2)}` : '—'}
                  </Mono>
                </div>
              ))
            )}
          </PreviewCard>

          {/* Active Sales Preview */}
          <PreviewCard
            title="Active Sales"
            accent="var(--g-warn)"
            onViewAll={() => onNavigate('Sales & Discounts')}
            footer={!quickLoading && !quickError && activeSaleCount > 5 ? (
              <button
                onClick={() => onNavigate('Sales & Discounts')}
                style={{
                  borderTop: '1px solid var(--g-border)',
                  padding: '10px 18px',
                                    fontSize: 12,
                  color: 'var(--g-text-muted)',
                  background: 'transparent',
                  border: 'none',
                  borderTopWidth: 1,
                  cursor: 'pointer',
                  textAlign: 'center',
                  letterSpacing: '0.01em',                                   }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--g-warn)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--g-text-muted)'}
              >
                + {(activeSaleCount - 5).toLocaleString()} more active sales
              </button>
            ) : null}
          >
            {quickLoading ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <Mono size={12} color="var(--g-text-dim)">Loading…</Mono>
              </div>
            ) : quickError ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <Mono size={12} color="var(--g-warn)">Couldn't load data — try the Sales & Discounts page</Mono>
              </div>
            ) : activeSales.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <Mono size={12} color="var(--g-text-dim)">No active sales found</Mono>
              </div>
            ) : (
              activeSales.map((p, i) => {
                const tone = p.pct >= 30 ? 'danger' : p.pct >= 15 ? 'amber' : 'vine';
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 18px',
                      borderBottom: i === activeSales.length - 1 ? 'none' : '1px solid var(--g-border-faint)',
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <ScrollingName
                        text={p.name}
                        style={{ fontSize: 13, color: 'var(--g-text)', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}
                      />
                      <Mono size={12} color="var(--g-text-muted)" style={{ marginTop: 2, display: 'block' }}>
                        {[p.brand, p.category, storeNameById.get(p.store_id)].filter(Boolean).join(' · ')}
                      </Mono>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <Mono size={12} color="var(--g-text-faint)" style={{ textDecoration: 'line-through' }}>
                        ${p.reg.toFixed(2)}
                      </Mono>
                      <Mono size={13} color="var(--g-text)" style={{ fontWeight: 700 }}>${p.sale.toFixed(2)}</Mono>
                      <Pill tone={tone}>{p.pct.toFixed(0)}% off</Pill>
                    </div>
                  </div>
                );
              })
            )}
          </PreviewCard>

          {/* Best Sellers Preview */}
          <PreviewCard
            title="Competitor Best Sellers"
            accent="var(--g-accent-hi)"
            onViewAll={() => onNavigate('Best Sellers')}
          >
            {bestSellers === null ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <Mono size={12} color="var(--g-text-dim)">Loading…</Mono>
              </div>
            ) : topBestSellers.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <Mono size={12} color="var(--g-text-dim)">Estimates appear after the next nightly run</Mono>
              </div>
            ) : (
              topBestSellers.map((p, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 18px',
                    borderBottom: i === topBestSellers.length - 1 ? 'none' : '1px solid var(--g-border-faint)',
                  }}
                >
                  <Pill tone={p.avg_jane_rank != null ? 'vine' : 'amber'} mono style={{ flexShrink: 0, minWidth: 64, textAlign: 'center' }}>
                    {p.avg_jane_rank != null ? `#${Math.round(p.avg_jane_rank)} seller` : `${p.cycles} restock${p.cycles > 1 ? 's' : ''}`}
                  </Pill>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <ScrollingName
                      text={p.product_name}
                      style={{ fontSize: 13, color: 'var(--g-text)', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}
                    />
                    <Mono size={12} color="var(--g-text-muted)" style={{ marginTop: 2, display: 'block' }}>
                      {[p.brand, p.category, storeNameById.get(p.store_id)].filter(Boolean).join(' · ')}
                    </Mono>
                  </div>
                </div>
              ))
            )}
          </PreviewCard>
        </div>
      </div>
    </div>
  );
}
